import React, { useState, useEffect } from 'react';
import { Card, List, Tag, Space, Progress, Alert, Timeline } from 'antd';
import { CheckCircleOutlined, WarningOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { waterMeterService } from '../../services/api/water-meter.service';
import type { WaterMeter } from '../../types/waterMeter';
import dayjs from 'dayjs';

interface MeterHealthProps {
  meterId: number;
}

interface Recommendation {
  type: string;
  priority: 'high' | 'medium' | 'low';
  reason: string;
  recommended_action: string;
}

const MeterHealth: React.FC<MeterHealthProps> = ({ meterId }) => {
  const [loading, setLoading] = useState(false);
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [healthScore, setHealthScore] = useState(100);
  const [meterInfo, setMeterInfo] = useState<WaterMeter | null>(null);

  useEffect(() => {
    fetchMeterHealth();
  }, [meterId]);

  const fetchMeterHealth = async () => {
    try {
      setLoading(true);
      const [recResponse, meterResponse] = await Promise.all([
        waterMeterService.getMaintenanceRecommendations(meterId),
        waterMeterService.getMeter(meterId)
      ]);

      setRecommendations(recResponse.data);
      setMeterInfo(meterResponse.data);

      // Calculate health score based on recommendations
      const score = calculateHealthScore(recResponse.data);
      setHealthScore(score);
    } catch (error) {
      console.error('Error fetching meter health:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateHealthScore = (recs: Recommendation[]): number => {
    let score = 100;
    recs.forEach(rec => {
      switch (rec.priority) {
        case 'high':
          score -= 30;
          break;
        case 'medium':
          score -= 15;
          break;
        case 'low':
          score -= 5;
          break;
      }
    });
    return Math.max(0, score);
  };

  const getScoreColor = (score: number): string => {
    if (score >= 80) return '#52c41a';
    if (score >= 60) return '#faad14';
    return '#f5222d';
  };

  const getPriorityColor = (priority: string): string => {
    switch (priority) {
      case 'high':
        return '#f5222d';
      case 'medium':
        return '#faad14';
      case 'low':
        return '#52c41a';
      default:
        return '#1890ff';
    }
  };

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'high':
        return <WarningOutlined style={{ color: '#f5222d' }} />;
      case 'medium':
        return <ClockCircleOutlined style={{ color: '#faad14' }} />;
      case 'low':
        return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      <Card loading={loading}>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold">Status vodomjera</h3>
            <p className="text-gray-500">
              ID: {meterInfo?.meter_id} | Instaliran: {dayjs(meterInfo?.created_at).format('DD.MM.YYYY')}
            </p>
          </div>
          <Progress
            type="circle"
            percent={healthScore}
            strokeColor={getScoreColor(healthScore)}
            size={80}
          />
        </div>

        {healthScore < 80 && (
          <Alert
            message="Potrebna pažnja"
            description="Ovaj vodomjer zahtijeva održavanje ili provjeru."
            type={healthScore < 60 ? 'error' : 'warning'}
            showIcon
            className="mb-4"
          />
        )}

        <Timeline className="mt-6">
          {recommendations.map((rec, index) => (
            <Timeline.Item
              key={index}
              color={getPriorityColor(rec.priority)}
              dot={getPriorityIcon(rec.priority)}
            >
              <div>
                <Space className="mb-2">
                  <Tag color={getPriorityColor(rec.priority)}>
                    {rec.type.toUpperCase()}
                  </Tag>
                  <Tag color={getPriorityColor(rec.priority)}>
                    {rec.priority.toUpperCase()}
                  </Tag>
                </Space>
                <p className="font-semibold">{rec.reason}</p>
                <p className="text-gray-600">{rec.recommended_action}</p>
              </div>
            </Timeline.Item>
          ))}

          {recommendations.length === 0 && (
            <Timeline.Item color="green">
              <p>Nema aktivnih preporuka za održavanje</p>
            </Timeline.Item>
          )}
        </Timeline>
      </Card>
    </div>
  );
};

export default MeterHealth;